import { FC, useCallback } from 'react';

import { Grid, xcss } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/responsive';

import { JiraOverlayShim } from '../onboarding-wizard/wizard/jira-overlay-shim';

import { LeftHandSideWrapper } from '../onboarding-wizard/common/ui/left-hand-side-wrapper/left-hand-side-wrapper';
import { RightHandSideWrapper } from '../onboarding-wizard/common/ui/right-hand-side-wrapper/right-hand-side-wrapper';

import { LeftHandSideCodingAgentChoice } from './left-hand-side/left-hand-side';
import { RightHandSideCodingAgentChoice } from './right-hand-side/right-hand-side';
import { CodingAgentId } from './types';
import { useCodingAgentSelection } from './use-coding-agent-selection';

export interface CodingAgentChoiceProps {
	/** Initial selection (e.g. when re-mounted with prior state). */
	readonly initialSelection?: CodingAgentId[];
	/** Project name to display in the right-hand-side Jira mock. */
	readonly projectName?: string;
	/** Called when the user confirms their selection. */
	readonly onSubmit: (selected: CodingAgentId[]) => void;
	/** Called when an agent is toggled, with its resulting selected state. */
	readonly onAgentToggled?: (agentId: CodingAgentId, selected: boolean) => void;
}

/**
 * TWCG-1935 / JFIND-Q4FY26: WAC confirmation-page screen that asks
 * new Jira users which coding agents they use.
 *
 * Renders inside the same {@link JiraOverlayShim} modal used by the
 * onboarding-wizard pages, with the matching LHS/RHS split:
 *  - LHS: heading + multi-select agent cards + primary "Next" CTA
 *  - RHS: Jira project mock SVG illustration
 *
 * The Grid with 50/50 columns on sm+ creates the side-by-side layout,
 * matching the pattern used by WelcomeModalPageSwitcher for
 * page-work-types, page-board-columns, etc.
 */
export const CodingAgentChoiceView: FC<CodingAgentChoiceProps> = ({
	initialSelection,
	projectName,
	onSubmit,
	onAgentToggled,
}) => {
	const { selected, toggle } = useCodingAgentSelection(initialSelection);

	const handleToggle = useCallback(
		(id: CodingAgentId) => {
			toggle(id);
			onAgentToggled?.(id, !selected.includes(id));
		},
		[toggle, onAgentToggled, selected],
	);

	const handleNext = useCallback(() => {
		onSubmit(selected);
	}, [onSubmit, selected]);

	return (
		<JiraOverlayShim>
			<Grid gap="space.0" xcss={gridStyles}>
				<LeftHandSideWrapper>
					<LeftHandSideCodingAgentChoice
						selected={selected}
						onToggle={handleToggle}
						onClickNext={handleNext}
					/>
				</LeftHandSideWrapper>
				<RightHandSideWrapper>
					<RightHandSideCodingAgentChoice projectName={projectName} selectedAgents={selected} />
				</RightHandSideWrapper>
			</Grid>
		</JiraOverlayShim>
	);
};

const gridStyles = xcss({
	width: '100%',
	height: '100%',
	gridTemplateColumns: '100%',
	[media.above.sm]: {
		gridTemplateColumns: '50% 50%',
	},
});

export default CodingAgentChoiceView;
